import { Badge, Box, Button, HStack, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type DeclaredExclusionGroundDto,
  type ExclusionGroundsAssessmentDto,
  ExclusionObligation,
} from "../../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";

const CHIP_COLORS: Record<ExclusionObligation, { bg: string; fg: string }> = {
  [ExclusionObligation.Mandatory]: { bg: "smChipRedBg", fg: "smChipRedFg" },
  [ExclusionObligation.Conditional]: { bg: "smChipAmberBg", fg: "smChipAmberFg" },
  [ExclusionObligation.Voluntary]: { bg: "smChipBlueBg", fg: "smChipBlueFg" },
};

export const DeclaredExclusionGrounds = ({
  grounds,
  onShowEspd,
}: {
  grounds: DeclaredExclusionGroundDto[];
  onShowEspd?: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <HStack justify="space-between" mb="2" align="baseline">
        <Text fontSize="sm" fontWeight="medium" color="smText">
          {t("Declared exclusion grounds")}
        </Text>
        {onShowEspd && (
          <Button variant="link" fontSize="sm" fontWeight="normal" color="smPrimary" onClick={onShowEspd}>
            {t("See the Exclusion grounds tab")}
          </Button>
        )}
      </HStack>
      <Stack spacing="3" as="ul" listStyleType="none">
        {grounds.map((ground) => (
          <Box as="li" key={ground.criterionId} borderWidth="1px" borderColor="smBorder" rounded="md" p="3">
            <Text fontSize="sm" fontWeight="medium" color="smText">
              {ground.name}
            </Text>
            <Text fontSize="xs" color="smMuted" mb="2">
              {ground.answeredYes ? t("Answer: Yes") : t("Answer: Not answered")}
            </Text>
            <Badge
              rounded="full"
              px="2.5"
              py="0.5"
              fontSize="xs"
              fontWeight="semibold"
              textTransform="none"
              whiteSpace="normal"
              bg={CHIP_COLORS[ground.obligation].bg}
              color={CHIP_COLORS[ground.obligation].fg}
            >
              {ground.label}
            </Badge>
            <Text fontSize="sm" color="smText" mt="2">
              {ground.lawfulIf}
            </Text>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export const ExclusionGroundsAssessmentRecord = ({ assessment }: { assessment: ExclusionGroundsAssessmentDto }) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();

  return (
    <Box>
      <Text fontSize="sm" fontWeight="medium" color="smText" mb="1">
        {t("Assessment of exclusion grounds")}
      </Text>
      <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
        {assessment.text}
      </Text>
      <Text fontSize="xs" color="smMuted" mt="1">
        {assessment.writtenByName
          ? t("Written by {{name}} · {{date}}", {
              name: assessment.writtenByName,
              date: displayDateWithTime(assessment.writtenAt),
            })
          : displayDateWithTime(assessment.writtenAt)}
      </Text>
    </Box>
  );
};
